import { useCallback, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const TOOLTIP_GAP = 8;
const VIEWPORT_PADDING = 8;

function positionFromAnchor(anchor, tooltip) {
    const rect = anchor.getBoundingClientRect();
    const tooltipWidth = tooltip?.offsetWidth ?? 0;
    const tooltipHeight = tooltip?.offsetHeight ?? 0;
    const spaceRight = window.innerWidth - rect.right;
    const placeLeft = tooltipWidth > 0
        && spaceRight < tooltipWidth + TOOLTIP_GAP
        && rect.left > tooltipWidth + TOOLTIP_GAP;
    const left = placeLeft
        ? rect.left - tooltipWidth - TOOLTIP_GAP
        : rect.right + TOOLTIP_GAP;
    const maxTop = window.innerHeight - tooltipHeight - VIEWPORT_PADDING;
    const top = tooltipHeight > 0
        ? Math.max(VIEWPORT_PADDING, Math.min(rect.top, maxTop))
        : rect.top;

    return { top, left };
}

/**
 * Wraps a list row and shows `content` in a fixed overlay on hover.
 * Position is measured from the row so the tooltip is not clipped by overflow:auto.
 */
export default function ListItemHoverTooltip({ children, content, disabled = false }) {
    const [visible, setVisible] = useState(false);
    const [coords, setCoords] = useState({ top: 0, left: 0 });
    const anchorRef = useRef(null);
    const tooltipRef = useRef(null);
    const tooltipId = useId();
    const show = Boolean(content) && visible && !disabled;

    const updatePosition = useCallback(() => {
        const anchor = anchorRef.current;
        if (!anchor) return;
        setCoords(positionFromAnchor(anchor, tooltipRef.current));
    }, []);

    useLayoutEffect(() => {
        if (!show) return;
        updatePosition();
        window.addEventListener('scroll', updatePosition, true);
        window.addEventListener('resize', updatePosition);
        return () => {
            window.removeEventListener('scroll', updatePosition, true);
            window.removeEventListener('resize', updatePosition);
        };
    }, [show, updatePosition]);

    return (
        <div
            ref={anchorRef}
            onMouseEnter={() => {
                if (disabled || !content) return;
                if (anchorRef.current) {
                    setCoords(positionFromAnchor(anchorRef.current, tooltipRef.current));
                }
                setVisible(true);
            }}
            onMouseLeave={() => setVisible(false)}
            aria-describedby={show ? tooltipId : undefined}
        >
            {children}
            {show && createPortal(
                <div
                    ref={tooltipRef}
                    id={tooltipId}
                    role="tooltip"
                    className="list-item-hover-tooltip"
                    style={{
                        top: coords.top,
                        left: coords.left
                    }}
                >
                    {content}
                </div>,
                document.body
            )}
        </div>
    );
}
